import { Box, styled } from '@mui/material';
import { memo, useCallback, useMemo, useRef, useState } from 'react';

import { ESeverity, useManifest } from '#shared';

import { AircraftDrawer } from '../../AircraftDrawer';
import { useAircrafts } from '../../data-context';
import { groupByAvailability, headerTextMap, sortByOrder } from '../lib';
import {
  AVAILABILITY_CARD_MIN_HEIGHT,
  WIDGET_AVAILABILITY_CARD_MIN_WIDTH,
  useRowsDistribution,
} from '../model';

import { AircraftCardAdapter } from './AircraftCardAdapter';
import { SeverityRow } from './SeverityRow';

import type { AircraftData } from '#entities';

const MAX_GRID_WIDTH = 1400;
const CARD_MIN_WIDTH = WIDGET_AVAILABILITY_CARD_MIN_WIDTH;

const Root = styled(Box)(({ theme: { spacing } }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing(1),
  paddingBlock: spacing(1),
  flex: 1,
  minHeight: 0,
  height: '100%',
  overflow: 'hidden',
}));

const RowsViewport = styled(Box)(({ theme: { spacing } }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing(1),
  flex: 1,
  minHeight: 0,
}));

const VerticalCardList = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(1),
  justifyContent: 'center',
  gridTemplateColumns: `repeat(auto-fit, minmax(${CARD_MIN_WIDTH}px, 1fr))`,
  maxWidth: MAX_GRID_WIDTH,
  margin: '0 auto',
  width: '100%',
  height: '100%',
  maxHeight: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: theme.spacing(0, 1),
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
}));

const ROW_GAP = 8;
const ROW_MIN = AVAILABILITY_CARD_MIN_HEIGHT;

interface CrewAvailabilityBoardProps {
  once?: boolean;
}

export const CrewAvailabilityBoard = memo<CrewAvailabilityBoardProps>(
  ({ once = false }) => {
    useManifest();
    const aircrafts = useAircrafts();

    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<AircraftData | null>(
      aircrafts[0] ?? null,
    );

    const aircraftsSorted = useMemo(() => sortByOrder(aircrafts), [aircrafts]);
    const groups = useMemo(
      () => groupByAvailability(aircraftsSorted),
      [aircraftsSorted],
    );

    const rowsRef = useRef<HTMLDivElement | null>(null);
    const rowsBy = useRowsDistribution(rowsRef, groups);

    // Find the maximum number of rows needed for any severity group
    const maxRows = Math.max(
      rowsBy[ESeverity.NA],
      rowsBy[ESeverity.CRITICAL],
      rowsBy[ESeverity.MAJOR],
      rowsBy[ESeverity.LOW],
    );

    const bodyHeight = useCallback(
      (rows: number) =>
        rows > 0 ? rows * ROW_MIN + Math.max(0, rows - 1) * ROW_GAP : 0,
      [],
    );

    const perRowHeight = useCallback(
      (rows: number) =>
        rows > 0
          ? Math.floor(
              (bodyHeight(rows) - Math.max(0, rows - 1) * ROW_GAP) / rows,
            )
          : 0,
      [bodyHeight],
    );

    const uniformPerRowHeight = useMemo(
      () => perRowHeight(maxRows),
      [maxRows, perRowHeight],
    );

    const handleSelect = (a: AircraftData) => {
      setSelected(a);
      setOpen(true);
    };
    const handleClose = () => setOpen(false);

    return (
      <Root>
        {once ? (
          <VerticalCardList>
            {aircraftsSorted.map(item => (
              <AircraftCardAdapter
                key={item.Id}
                data={item}
                width="100%"
                fullSize
                onClickId={handleSelect}
              />
            ))}
          </VerticalCardList>
        ) : (
          <RowsViewport ref={rowsRef}>
            <SeverityRow
              title={headerTextMap.NA}
              groupKey={ESeverity.NA}
              items={groups.NA}
              rowsCount={rowsBy[ESeverity.NA]}
              onSelect={handleSelect}
              perRowHeight={() => uniformPerRowHeight}
            />
            <SeverityRow
              title={headerTextMap.CRITICAL}
              groupKey={ESeverity.CRITICAL}
              items={groups.CRITICAL}
              rowsCount={rowsBy[ESeverity.CRITICAL]}
              onSelect={handleSelect}
              perRowHeight={() => uniformPerRowHeight}
            />
            <SeverityRow
              title={headerTextMap.MAJOR}
              groupKey={ESeverity.MAJOR}
              items={groups.MAJOR}
              rowsCount={rowsBy[ESeverity.MAJOR]}
              onSelect={handleSelect}
              perRowHeight={() => uniformPerRowHeight}
            />
            <SeverityRow
              title={headerTextMap.LOW}
              groupKey={ESeverity.LOW}
              items={groups.LOW}
              rowsCount={rowsBy[ESeverity.LOW]}
              onSelect={handleSelect}
              perRowHeight={() => uniformPerRowHeight}
            />
          </RowsViewport>
        )}

        {selected && (
          <AircraftDrawer
            open={open}
            onClose={handleClose}
            variant="persistent"
            aircraftData={selected}
            anchor="right"
          >
            <AircraftDrawer.AircraftEvents />
            <AircraftDrawer.Location />
            <AircraftDrawer.Timeline />
            <AircraftDrawer.Weather />
          </AircraftDrawer>
        )}
      </Root>
    );
  },
);

CrewAvailabilityBoard.displayName = 'CrewAvailabilityBoard';
